import { useEffect, useRef, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useAppDispatch } from "src/store/store";
import {
  generatedApi,
  useGeocodeStationQuery,
} from "src/store/api/generatedApi";
import type {
  RailRouteResponse,
  MillInfo,
  Coordinates,
} from "src/store/api/generatedApi";

interface UseRailRoutesResult {
  originCoordinates: Coordinates | null;
  routes: Record<string, RailRouteResponse>;
  isLoading: boolean;
  error: string | null;
  geocodeError: string | null;
}

export const useRailRoutes = (
  stationName: string | null,
  stationState: string | null,
  mills: MillInfo[]
): UseRailRoutesResult => {
  const dispatch = useAppDispatch();
  const [routes, setRoutes] = useState<Record<string, RailRouteResponse>>({});
  const [routesLoading, setRoutesLoading] = useState(false);
  const generationRef = useRef(0);

  const geocodeArg =
    stationName && stationState ? { stationName, stationState } : skipToken;

  const {
    data: geocodeData,
    isFetching: geocodeFetching,
    error: geocodeError,
  } = useGeocodeStationQuery(geocodeArg);

  const originCoordinates =
    geocodeData && !geocodeFetching && !geocodeError
      ? geocodeData.location
      : null;

  useEffect(() => {
    if (!originCoordinates || mills.length === 0) {
      ++generationRef.current;
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- async data loading state machine
      setRoutes({});
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- async data loading state machine
      setRoutesLoading(false);
      return;
    }

    const generation = ++generationRef.current;
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- async data loading state machine
    setRoutesLoading(true);

    const subscriptions = mills.map((mill) =>
      dispatch(
        generatedApi.endpoints.findRailRoute.initiate(
          {
            fromLatitude: originCoordinates.latitude,
            fromLongitude: originCoordinates.longitude,
            toLatitude: mill.location.latitude,
            toLongitude: mill.location.longitude,
            railroad: "UP",
          },
          { subscribe: true, forceRefetch: false }
        )
      )
    );

    void Promise.all(
      mills.map((mill, i) =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        subscriptions[i]!.unwrap()
          .then((route) => ({ tenantName: mill.tenant_name, route }))
          .catch(() => ({ tenantName: mill.tenant_name, route: null }))
      )
    ).then((results) => {
      if (generationRef.current !== generation) return;

      setRoutes(
        Object.fromEntries(
          results
            .filter((r) => r.route != null)
            .map((r) => [r.tenantName, r.route])
        )
      );
      setRoutesLoading(false);
    });

    return () => {
      subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
    };
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- originCoordinates decomposed to avoid re-running when unrelated properties change
  }, [
    stationName,
    stationState,
    originCoordinates?.latitude,
    originCoordinates?.longitude,
    mills,
    dispatch,
  ]);

  const routesValid = !!originCoordinates && !geocodeError && mills.length > 0;

  return {
    originCoordinates,
    routes: routesValid ? routes : {},
    isLoading: routesLoading,
    geocodeError: geocodeError ? "logisticsErrorGeocodingStation" : null,
    error: null,
  };
};
